/**
 * Feedback Card Component (Enhanced)
 * 
 * Clickable card that opens detail modal.
 * Vote button appears clickable for all users.
 * Guests clicking vote → triggers auth modal.
 */
import { useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import VoteButton from './VoteButton';

// Helper to strip HTML tags for plain text preview
const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};

const FeedbackCard = ({ 
    item, 
    onUpvote, 
    onDownvote,
    onCardClick, 
    isLoggedIn, 
    currentUser,
    isPremium,
    enableDownvoting = false,
    onAuthRequired,
    statuses = {}
}) => {
    // Build dynamic status label/color from prop.
    const getStatusLabel = (slug) => {
        if (statuses[slug] && statuses[slug].label) return statuses[slug].label;
        return slug;
    };

    const getStatusColor = (slug) => {
        const fallback = { bg: '#f3f4f640', text: '#6b7280' };
        if (statuses[slug] && statuses[slug].color) {
            return { bg: statuses[slug].color + '20', text: statuses[slug].color };
        }
        return fallback;
    };

    const categoryLabels = {
        'feature_requests': __( 'Feature Request', 'changeloger' ),
        'bug_reports': __( 'Bug Report', 'changeloger' ),
        'integrations': __( 'Integration', 'changeloger' ),
        'languages': __( 'Language', 'changeloger' )
    };

    const status = item.status || 'in_review';
    const statusLabel = getStatusLabel(status);
    const category = item.category?.slug || item.category || 'feature_requests';
    const currentStatusColor = getStatusColor(status);

    // Get plain text excerpt (strip HTML)
    const excerpt = stripHtml(item.excerpt || item.content);

    // Format relative date
    const getRelativeDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
        
        if (diff === 0) return __( 'Today', 'changeloger' );
        if (diff === 1) return __( 'Yesterday', 'changeloger' );
        if (diff < 7) return `${diff} days ago`;
        return `About ${Math.floor(diff / 7)} weeks ago`;
    };

    const handleCardClick = (e) => {
        // Don't trigger if clicking vote button
        if (e.target.closest('.cha-feedback-card-vote')) return;
        if (onCardClick) onCardClick(item);
    };

    return (
        <div className="cha-feedback-card" onClick={handleCardClick}>
            {/* Vote Section */}
            <div className="cha-feedback-card-vote" onClick={(e) => e.stopPropagation()}>
                <VoteButton 
                    upvotes={item.upvotes || 0}
                    downvotes={item.downvotes || 0}
                    hasUpvoted={item.has_upvoted || item.has_voted}
                    hasDownvoted={item.has_downvoted}
                    isPremium={isPremium}
                    onUpvote={() => onUpvote && onUpvote(item.id)}
                    onDownvote={() => onDownvote && onDownvote(item.id)}
                    isLoggedIn={isLoggedIn}
                    enableDownvoting={enableDownvoting}
                    onAuthRequired={onAuthRequired}
                />
            </div>
            
            {/* Content */}
            <div className="cha-feedback-card-content">
                {/* Status Badge */}
                <span 
                    className="cha-feedback-status-badge"
                    style={{ 
                        backgroundColor: currentStatusColor.bg,
                        color: currentStatusColor.text
                    }}
                >
                    {statusLabel}
                </span>

                {/* Title */}
                <h3 className="cha-feedback-card-title">{item.title}</h3>
                
                {/* Excerpt */}
                {excerpt && (
                    <p className="cha-feedback-card-excerpt">
                        {excerpt.length > 120 ? excerpt.substring(0, 120) + '...' : excerpt}
                    </p>
                )}
                
                {/* Meta */}
                <div className="cha-feedback-card-meta">
                     
                    {/* Author */}
                    <span className="cha-feedback-author">
                        {item.author?.avatar && (
                            <img src={item.author.avatar} alt="" className="cha-avatar-sm" />
                        )}
                        {item.author?.name || __( 'An Anonymous User', 'changeloger' )}
                    </span>
                    
                    
                    {/* Comments Count */}
                    <span className="cha-feedback-comments">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="14" height="14">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        {item.comment_count || 0}
                    </span>
                   
                    {/* Date */}
                    <span className="cha-feedback-date">
                        {getRelativeDate(item.date)}
                    </span>
                    
                    {/* Category */}
                    <span className="cha-feedback-category">
                        <span className="cha-category-icon">💡</span>
                        {categoryLabels[category] || category}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default FeedbackCard;
